import { Component, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { FormBuilder, FormGroup } from '@angular/forms';
import { Subscription } from 'rxjs';
import { tap } from 'rxjs/operators';
import { HelpersService, HttpBaseService } from '@evento-ng/libs';
import { NgbCarouselConfig } from '@ng-bootstrap/ng-bootstrap';
import { NgxSpinnerService } from 'ngx-spinner';
import {
  championsApi,
  commonCitiesApi,
  matchesApi,
  stadiumsApi,
  teamsApi,
} from 'src/app/shared/constants/api.constants';
import { BsDaterangepickerDirective } from 'ngx-bootstrap/datepicker';
import { DatePipe } from '@angular/common';
import { TranslateService } from '@ngx-translate/core';

@Component({
  selector: 'app-matches-list',
  templateUrl: './matches-list.component.html',
  styleUrls: ['./matches-list.component.scss'],
  providers: [DatePipe],
})
export class MatchesListComponent implements OnInit, OnDestroy {
  matchesFiltersForm: FormGroup;
  matches: any[] = [];
  matchesMeta: any;
  page: number = 1;
  champions: any[] = [];
  teams: any[] = [];
  stadiums: any[] = [];
  cities: any[] = [];
  selectedDateRange: any;
  today = new Date();
  minDate = new Date();
  maxDate = new Date();
  subs: Subscription[] = [];
  isMobile = false;
  showFilters = false;
  @ViewChild('datePicker', { static: false })
  datePicker!: BsDaterangepickerDirective;
  isLoadingMatches = false;

  constructor(
    private formBuilder: FormBuilder,
    private dataService: HttpBaseService,
    private helpers: HelpersService,
    private spinner: NgxSpinnerService,
    private datePipe: DatePipe,
    private translateService: TranslateService,
    config: NgbCarouselConfig
  ) {
    config.interval = 10000;
    config.wrap = false;
    config.keyboard = true;
    config.pauseOnHover = false;
  }

  ngOnInit(): void {
    this.checkScreenSize();
    window.addEventListener('resize', this.checkScreenSize.bind(this));
    this.initForm();
    this.loadData();
  }

  checkScreenSize(): void {
    this.isMobile = window.innerWidth <= 1024;
  }

  toggleFilters(): void {
    this.showFilters = !this.showFilters;
  }

  ngAfterViewInit() {
    if (this.datePicker) {
      this.datePicker.hide();
    }
  }

  openDatePicker() {
    if (this.datePicker) {
      this.datePicker.show();
    }
  }

  onDateSelected() {
    if (this.datePicker) {
      this.datePicker.hide();
    }
  }

  private initForm(): void {
    this.maxDate.setDate(this.today.getDate() + 7);

    this.matchesFiltersForm = this.formBuilder.group({
      team: [null],
      champion: [null],
      city: [null],
      stadium: [null],
      date: [null],
    });

    this.updateSelectedDateRange();

    this.subs.push(
      this.matchesFiltersForm.valueChanges
        .pipe(
          tap(() => {
            this.updateSelectedDateRange();
            this.getMatches();
          })
        )
        .subscribe()
    );
  }

  private updateSelectedDateRange(): void {
    const dateRange = this.matchesFiltersForm.get('date')?.value;
    if (dateRange && dateRange.length === 2) {
      const lang = this.translateService.currentLang || 'en';

      this.selectedDateRange = {
        from:
          lang === 'ar'
            ? this.formatDateInArabic(dateRange[0])
            : this.datePipe.transform(dateRange[0], 'd MMMM'),
        to:
          lang === 'ar'
            ? this.formatDateInArabic(dateRange[1])
            : this.datePipe.transform(dateRange[1], 'd MMMM yyyy'),
      };
    } else {
      this.selectedDateRange = null;
    }
  }

  private formatDateInArabic(date: Date): string {
    const arabicMonths = [
      'يناير',
      'فبراير',
      'مارس',
      'أبريل',
      'مايو',
      'يونيو',
      'يوليو',
      'أغسطس',
      'سبتمبر',
      'أكتوبر',
      'نوفمبر',
      'ديسمبر',
    ];

    const d = new Date(date);
    return `${d.getDate()} ${arabicMonths[d.getMonth()]} ${d.getFullYear()}`;
  }

  private loadData(): void {
    this.getTeams();
    this.getChampions();
    this.getCities();
    this.getStadiums();
    this.getMatches();
  }

  getChampions(): void {
    const params: any = {
      page: 1,
      take: 100,
    };
    this.dataService
      .get(`${championsApi}`, { params })
      .subscribe((champions) => {
        this.champions = champions?.data?.map((champion: any) => ({
          ...champion,
          name: this.helpers.getElementTranslatedName(champion),
        }));
      });
  }

  private getTeams(): void {
    const params: any = {
      page: 1,
      take: 100,
    };
    this.dataService.get(`${teamsApi}`, { params }).subscribe((teams) => {
      this.teams = teams?.data?.map((team: any) => ({
        ...team,
        name: this.helpers.getElementTranslatedName(team),
      }));
    });
  }

  private getStadiums(): void {
    const params: any = {
      page: 1,
      take: 100,
    };
    this.dataService.get(`${stadiumsApi}`, { params }).subscribe((stadiums) => {
      this.stadiums = stadiums?.data?.map((stadium: any) => ({
        ...stadium,
        name: this.helpers.getElementTranslatedName(stadium),
      }));
    });
  }

  private getCities(): void {
    this.dataService.get(`${commonCitiesApi}`).subscribe((cities) => {
      this.cities = cities?.map((city: any) => ({
        ...city,
        name: this.helpers.getElementTranslatedName(city),
      }));
    });
  }

  private getMatches(): void {
    this.isLoadingMatches = true;

    const { champion, stadium, team, city, date } =
      this.matchesFiltersForm.value;
    const params: any = {
      page: this.page,
      take: 12,
      championId: champion,
      stadiumId: stadium,
      teamId: team,
      cityId: city,
    };

    if (date && date.length === 2) {
      params.fromDate = new Date(date[0]).setHours(0, 0, 0, 0);
      params.toDate = new Date(date[1]).setHours(23, 59, 59, 999);
    }

    Object.keys(params).forEach((key) => {
      if (params[key] == null) delete params[key];
    });

    this.dataService.get(`${matchesApi}`, { params }).subscribe(
      (res) => {
        this.matchesMeta = res.meta;
        this.matches = res.data;
        this.isLoadingMatches = false;
      },
      (err) => {
        this.isLoadingMatches = false;
      }
    );
  }

  onFilterChange(): void {
    this.getMatches();
  }

  goToPage(n: number): void {
    this.page = n;
    this.getMatches();
  }

  onNext(): void {
    this.page++;
    this.getMatches();
  }

  onPrev(): void {
    this.page--;
    this.getMatches();
  }

  ngOnDestroy(): void {
    this.subs.forEach((sub) => sub.unsubscribe());
    window.removeEventListener('resize', this.checkScreenSize.bind(this));
  }
}
